import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { ScrollArea } from '../ui/scroll-area';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { XCircle, AlertTriangle, ArrowRight } from 'lucide-react';

const STATUS_COLORS = {
  create: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  update: 'bg-blue-100 text-blue-700 border-blue-200',
  error: 'bg-red-100 text-red-700 border-red-200',
  skip: 'bg-gray-100 text-gray-700 border-gray-200'
};

const STATUS_LABELS = {
  create: 'Nuovo',
  update: 'Aggiorna',
  error: 'Errore',
  skip: 'Ignora'
};

export function ImportPreviewStep({ validationResult, onBack, onCancel, onProceed }) {
  if (!validationResult) return null;

  const { total_rows, new_items, update_items, error_items, errors, warnings, preview_data } = validationResult;

  return (
    <div className="space-y-6">
      {/* Card riepilogo */}
      <div className="grid grid-cols-4 gap-3">
        <Card className="bg-gray-50">
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold">{total_rows}</div>
            <div className="text-xs text-muted-foreground">Righe totali</div>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-emerald-700">{new_items}</div>
            <div className="text-xs text-emerald-600">Nuovi articoli</div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-blue-700">{update_items}</div>
            <div className="text-xs text-blue-600">Da aggiornare</div>
          </CardContent>
        </Card>
        <Card className={`${error_items > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50'}`}>
          <CardContent className="pt-4 text-center">
            <div className={`text-2xl font-bold ${error_items > 0 ? 'text-red-700' : ''}`}>{error_items}</div>
            <div className={`text-xs ${error_items > 0 ? 'text-red-600' : 'text-muted-foreground'}`}>Errori</div>
          </CardContent>
        </Card>
      </div>

      {/* Alert errori */}
      {errors.length > 0 && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertTitle>Errori rilevati ({errors.length})</AlertTitle>
          <AlertDescription>
            <ScrollArea className="h-24 mt-2">
              <ul className="text-sm space-y-1">
                {errors.slice(0, 10).map((err) => (
                  <li key={`err-${err.row}-${err.item_code || err.message.substring(0, 15)}`}>
                    <strong>Riga {err.row}</strong>: {err.message}
                    {err.item_code && <span className="text-muted-foreground"> ({err.item_code})</span>}
                  </li>
                ))}
                {errors.length > 10 && (
                  <li className="text-muted-foreground">... e altri {errors.length - 10} errori</li>
                )}
              </ul>
            </ScrollArea>
          </AlertDescription>
        </Alert>
      )}

      {/* Alert avvisi */}
      {warnings.length > 0 && (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-700">Avvisi ({warnings.length})</AlertTitle>
          <AlertDescription className="text-amber-600">
            <ScrollArea className="h-20 mt-2">
              <ul className="text-sm space-y-1">
                {warnings.slice(0, 5).map((warn) => (
                  <li key={`warn-${warn.row}-${warn.message.substring(0, 15)}`}>
                    <strong>Riga {warn.row}</strong>: {warn.message}
                  </li>
                ))}
              </ul>
            </ScrollArea>
          </AlertDescription>
        </Alert>
      )}

      {/* Tabella anteprima */}
      <div>
        <h4 className="font-medium mb-2">Anteprima articoli</h4>
        <ScrollArea className="h-64 border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Riga</TableHead>
                <TableHead>Codice</TableHead>
                <TableHead>Tipologia</TableHead>
                <TableHead>Marca</TableHead>
                <TableHead>Modello</TableHead>
                <TableHead className="w-24">Azione</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {preview_data.slice(0, 50).map((item) => (
                <TableRow key={`preview-${item.row_number}-${item.item_code}`} className={item.action === 'error' ? 'bg-red-50' : ''}>
                  <TableCell className="font-mono text-xs">{item.row_number}</TableCell>
                  <TableCell className="font-mono text-xs">{item.item_code || '-'}</TableCell>
                  <TableCell className="text-xs">{item.tipologia || '-'}</TableCell>
                  <TableCell className="text-xs">{item.marca || '-'}</TableCell>
                  <TableCell className="text-xs">{item.modello || '-'}</TableCell>
                  <TableCell>
                    <Badge className={`text-xs ${STATUS_COLORS[item.action]}`}>
                      {STATUS_LABELS[item.action]}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollArea>
        {preview_data.length > 50 && (
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Mostrati 50 di {preview_data.length} articoli
          </p>
        )}
      </div>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          Indietro
        </Button>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onCancel}>
            Annulla
          </Button>
          <Button
            onClick={onProceed}
            disabled={error_items > 0}
            data-testid="proceed-import-btn"
          >
            {error_items > 0 ? 'Correggi errori' : 'Procedi'}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
